import React, { createContext, useContext, useState, useEffect } from 'react';
import { cn } from '../../../../lib/cn';
import { useTheme } from '../../../../context/ThemeContext';

const AlertDialogContext = createContext<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

export function AlertDialog({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  // Prevent background scrolling when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  return (
    <AlertDialogContext.Provider value={{ open, setOpen }}>
      {children}
    </AlertDialogContext.Provider>
  );
}

export function AlertDialogTrigger({ children, className, title }: { children: React.ReactNode, className?: string, title?: string }) {
  const { setOpen } = useContext(AlertDialogContext)!;
  return (
    <button onClick={() => setOpen(true)} className={className} title={title}>
      {children}
    </button>
  );
}

import { createPortal } from 'react-dom';

export function AlertDialogPopup({ children, className }: { children: React.ReactNode, className?: string }) {
  const { open, setOpen } = useContext(AlertDialogContext)!;

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" 
        onClick={() => setOpen(false)} 
      />
      <div
        className={cn(
          "relative z-10 w-full max-w-md p-6 mx-4 rounded-3xl animate-fade-in zoom-in-95 glass-strong",
          className
        )}
      >
        {/* Top inner highlight (Apple glass hallmark) matching GlassCard */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
        />
        {children}
      </div>
    </div>,
    document.body
  );
}

export function AlertDialogHeader({ children, className }: { children: React.ReactNode, className?: string }) {
  return <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)}>{children}</div>;
}

export function AlertDialogTitle({ children, className }: { children: React.ReactNode, className?: string }) {
  return <h2 className={cn("text-lg font-semibold page-title", className)}>{children}</h2>;
}

export function AlertDialogDescription({ children, className }: { children: React.ReactNode, className?: string }) {
  return <p className={cn("text-sm page-subtitle", className)}>{children}</p>;
}

export function AlertDialogFooter({ children, className }: { children: React.ReactNode, className?: string }) {
  return <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 mt-6", className)}>{children}</div>;
}

export function AlertDialogCancel({ children, className }: { children: React.ReactNode, className?: string }) {
  const { setOpen } = useContext(AlertDialogContext)!;
  
  return (
    <button
      onClick={() => setOpen(false)}
      className={cn("mt-2 sm:mt-0 btn-ghost", className)}
    >
      {children}
    </button>
  );
}

export function AlertDialogAction({ children, onClick, className }: { children: React.ReactNode, onClick?: () => void, className?: string }) {
  const { setOpen } = useContext(AlertDialogContext)!;
  
  // By default, make it a primary button unless overridden
  const baseClass = className?.includes('bg-red') || className?.includes('bg-rose') ? 'btn-critical' : 'btn-primary';
  
  return (
    <button
      onClick={(e) => {
        if (onClick) onClick();
        setOpen(false);
      }}
      className={cn(baseClass, className)}
    >
      {children}
    </button>
  );
}
